import { FiCheckCircle, FiCircle, FiEye, FiEyeOff } from 'react-icons/fi';

import {
  Title,
  HelperText,
  Panel,
  Block,
  FormGrid,
  Stack,
  CancelBtn,
} from '../account-settings.styled';
import { PasswordStrengthBar } from '../../../../../components/strength-bar/strength-bar';

import {
  Shell,
  Field,
  Label,
  InputWithToggle,
  InputWrap,
  EyeToggle,
  ActionBar,
  Checklist,
  CheckItem,
} from './change-password.styled';
import { useChangePasswordPanel } from './change-password.hooks';
import { OnboardBtn } from '../../profile/profile.styled';

type Props = {
  onPasswordChanged?: () => void;
  handleBack?: () => void;
  onCancel?: () => void; // optional: parent can close the panel
};

export default function ChangePasswordPanel({ onPasswordChanged, onCancel, handleBack }: Props) {
  const {
    current,
    next,
    confirm,
    error,
    saving,
    triedSubmit,
    reqs,
    strengthScore,
    isTooWeak,
    passwordsMatch,
    disabled,
    showCurrent,
    showNext,
    showConfirm,
    setCurrent,
    setNext,
    setConfirm,
    setShowCurrent,
    setShowNext,
    setShowConfirm,
    handleSubmit,
    handleCancel,
  } = useChangePasswordPanel({ onPasswordChanged, onCancel, handleBack });

  return (
    <Shell role="region" aria-label="Change password panel">
      <Panel aria-labelledby="change-password-title">
        <FormGrid>
          <Block>
            <Title id="change-password-title">Change password</Title>
            <Stack $gap={12}>
              <div spellCheck={false}>
                <Stack $gap={16}>
                  <Field>
                    <Label htmlFor="current-password">Current password</Label>
                    <InputWrap>
                      <InputWithToggle
                        id="current-password"
                        type={showCurrent ? 'text' : 'password'}
                        value={current}
                        onChange={(e) => setCurrent(e.target.value)}
                        autoComplete="current-password"
                        aria-required
                        $hasToggle
                      />
                      <EyeToggle
                        type="button"
                        aria-label={showCurrent ? 'Hide current password' : 'Show current password'}
                        aria-pressed={showCurrent}
                        aria-controls="current-password"
                        onClick={() => setShowCurrent((s) => !s)}
                      >
                        {showCurrent ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                      </EyeToggle>
                    </InputWrap>
                  </Field>

                  <Field>
                    <Label htmlFor="new-password">New password</Label>
                    <InputWrap>
                      <InputWithToggle
                        id="new-password"
                        type={showNext ? 'text' : 'password'}
                        value={next}
                        onChange={(e) => setNext(e.target.value)}
                        autoComplete="new-password"
                        aria-required
                        aria-describedby="password-strength password-checklist"
                        $hasToggle
                      />
                      <EyeToggle
                        type="button"
                        aria-label={showNext ? 'Hide new password' : 'Show new password'}
                        aria-pressed={showNext}
                        aria-controls="new-password"
                        onClick={() => setShowNext((s) => !s)}
                      >
                        {showNext ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                      </EyeToggle>
                    </InputWrap>
                  </Field>

                  <Field>
                    <Label htmlFor="confirm-password">Confirm new password</Label>
                    <InputWrap>
                      <InputWithToggle
                        id="confirm-password"
                        type={showConfirm ? 'text' : 'password'}
                        value={confirm}
                        onChange={(e) => setConfirm(e.target.value)}
                        autoComplete="new-password"
                        aria-required
                        $hasToggle
                      />
                      <EyeToggle
                        type="button"
                        aria-label={
                          showConfirm ? 'Hide confirmation password' : 'Show confirmation password'
                        }
                        aria-pressed={showConfirm}
                        aria-controls="confirm-password"
                        onClick={() => setShowConfirm((s) => !s)}
                      >
                        {showConfirm ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                      </EyeToggle>
                    </InputWrap>

                    {/* Strength bar */}
                    <div id="password-strength" style={{ marginTop: 6 }}>
                      <PasswordStrengthBar score={strengthScore} allRequirementsMet={reqs.length && reqs.upper && reqs.lower && reqs.number && reqs.symbol} />
                    </div>

                    {/* Checklist */}
                    <Checklist id="password-checklist">
                      <CheckItem $ok={reqs.length}>
                        <span className="icon">
                          {reqs.length ? <FiCheckCircle size={14} /> : <FiCircle size={14} />}
                        </span>
                        8&nbsp;+ characters
                      </CheckItem>
                      <CheckItem $ok={reqs.upper}>
                        <span className="icon">
                          {reqs.upper ? <FiCheckCircle size={14} /> : <FiCircle size={14} />}
                        </span>
                        Upper-case letter
                      </CheckItem>
                      <CheckItem $ok={reqs.lower}>
                        <span className="icon">
                          {reqs.lower ? <FiCheckCircle size={14} /> : <FiCircle size={14} />}
                        </span>
                        Lower-case letter
                      </CheckItem>
                      <CheckItem $ok={reqs.number}>
                        <span className="icon">
                          {reqs.number ? <FiCheckCircle size={14} /> : <FiCircle size={14} />}
                        </span>
                        Number
                      </CheckItem>
                      <CheckItem $ok={reqs.symbol}>
                        <span className="icon">
                          {reqs.symbol ? <FiCheckCircle size={14} /> : <FiCircle size={14} />}
                        </span>
                        Symbol&nbsp;(!@#$…)
                      </CheckItem>
                    </Checklist>

                    {(error || (triedSubmit && isTooWeak) || (triedSubmit && !passwordsMatch)) && (
                      <HelperText role="alert">
                        {error ||
                          (triedSubmit && !passwordsMatch ? 'Passwords do not match.' : '') ||
                          (triedSubmit && isTooWeak ? 'Password too weak. Add upper/lower case letters, numbers and symbols.' : '')}
                      </HelperText>
                    )}
                  </Field>

                  {/* Bottom actions: left Cancel | right Save */}
                  <ActionBar>
                    <CancelBtn
                      onClick={(e) => {
                        e.preventDefault();
                        handleCancel();
                      }}
                      aria-label="Cancel password change"
                    >
                      Cancel
                    </CancelBtn>
                    <OnboardBtn
                      style={{ width: '142px' }}
                      type="button"
                      disabled={disabled}
                      onClick={handleSubmit}
                      aria-label="Save new password"
                    >
                      {saving ? 'Saving…' : 'Save password'}
                    </OnboardBtn>
                  </ActionBar>
                </Stack>
              </div>
            </Stack>
          </Block>
        </FormGrid>
      </Panel>
    </Shell>
  );
}
